import { Send } from "lucide-react";
import { type FormEvent, type ReactElement, useRef, useState } from "react";

import { Button } from "@/common/ui/button";
import { focusOutline } from "@/common/ui/focus-outline";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
}

export function ChatInput({ onSend, disabled }: ChatInputProps): ReactElement {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setValue("");
    inputRef.current?.focus();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("flex items-center gap-2 border-t border-border p-3")}
      data-testid="chat-input-form"
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Type a message..."
        aria-label="Message"
        disabled={disabled}
        className={cn(
          "flex-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm",
          "placeholder:text-muted-foreground",
          focusOutline,
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
        data-testid="chat-input"
      />
      <Button
        type="submit"
        size="icon"
        variant="ghost"
        disabled={disabled || !value.trim()}
        data-testid="chat-send-button"
        title="Send message"
      >
        <Send className="h-4 w-4" />
      </Button>
    </form>
  );
}
